import { IProductPrice } from '@/types';
import { COMPARE_COMPROVE_CONFIG } from '@/lib/hooks/compare-comprove-config';

/** Built once — avoids per-call Map allocation in hot paths (cart, PDP). */
const ACTIVE_COMPARE_CONFIG_BY_CHANNEL_ID = new Map(
  COMPARE_COMPROVE_CONFIG.filter((c) => c.active).map((c) => [c.id, c]),
);

/** Channels used for other flows — never listed as competitors */
export const COMPARE_EXCLUDED_CHANNEL_IDS = new Set([1, 14, 15]);

export const PLATFORM_PRICE_CHANNEL_ID = 1;

function normalizePrice(value: unknown): number | null {
  if (value === undefined || value === null) return null;
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Our reference price: `sale_price` from the platform row (price_channel_id 1).
 * Falls back to product-level sale/price when the platform row is missing or invalid.
 */
export function getPlatformSalePrice(
  product_prices: IProductPrice[] | undefined,
  fallback?: { sale_price?: number; price?: number },
): number | null {
  const row = product_prices?.find(
    (p) => p.price_channel_id === PLATFORM_PRICE_CHANNEL_ID,
  );
  if (row) {
    const n = normalizePrice(row.sale_price ?? row.price);
    if (n !== null && n > 0) return n;
  }
  const fb = fallback?.sale_price ?? fallback?.price;
  const n = normalizePrice(fb);
  return n !== null && n > 0 ? n : null;
}

export type CompareCompetitorRow = {
  id: string;
  price_channel_id: number;
  image: string;
  /** Competitor price shown (sale_price, or price if needed) */
  value: number;
  /** value - our platform sale price */
  savings: number;
};

/**
 * Competitors to show: positive price, strictly higher than our platform sale price,
 * not in excluded channels, and with a matching entry in COMPARE_COMPROVE_CONFIG (images).
 */
export function buildCompareCompetitorRows(
  product_prices: IProductPrice[] | undefined,
  fallback?: { sale_price?: number; price?: number },
): {
  rows: CompareCompetitorRow[];
  ourSalePrice: number | null;
  economiaMedia: number;
} {
  const ourSalePrice = getPlatformSalePrice(product_prices, fallback);
  if (ourSalePrice === null || !product_prices?.length) {
    return { rows: [], ourSalePrice, economiaMedia: 0 };
  }

  const rows: CompareCompetitorRow[] = [];

  for (const pp of product_prices) {
    const channelId = pp.price_channel_id;
    if (COMPARE_EXCLUDED_CHANNEL_IDS.has(channelId)) continue;

    const cfg = ACTIVE_COMPARE_CONFIG_BY_CHANNEL_ID.get(channelId);
    if (!cfg) continue;

    const competitorPrice = normalizePrice(pp.sale_price ?? pp.price);
    if (competitorPrice === null || competitorPrice <= 0) continue;
    if (competitorPrice <= ourSalePrice) continue;

    rows.push({
      id: String(channelId),
      price_channel_id: channelId,
      image: cfg.image,
      value: competitorPrice,
      savings: competitorPrice - ourSalePrice,
    });
  }

  rows.sort((a, b) => a.value - b.value);

  const economiaMedia =
    rows.length > 0
      ? rows.reduce((sum, r) => sum + r.savings, 0) / rows.length
      : 0;

  return { rows, ourSalePrice, economiaMedia };
}

export function hasCompareCompetitors(
  product_prices: IProductPrice[] | undefined,
  fallback?: { sale_price?: number; price?: number },
): boolean {
  return buildCompareCompetitorRows(product_prices, fallback).rows.length > 0;
}

export type CartCompareLineInput = {
  quantity?: number;
  product_prices?: IProductPrice[];
  sale_price?: number;
  price?: number;
};

/**
 * Same rules as buildCompareCompetitorRows, but only returns the highest qualifying
 * competitor unit price (no row objects, no sort) — optimized for cart sidebar.
 */
function getMaxQualifyingCompetitorUnitPrice(
  product_prices: IProductPrice[] | undefined,
  fallback?: { sale_price?: number; price?: number },
): { maxUnit: number | null; ourSalePrice: number | null } {
  const ourSalePrice = getPlatformSalePrice(product_prices, fallback);
  if (ourSalePrice === null || !product_prices?.length) {
    return { maxUnit: null, ourSalePrice };
  }

  let maxUnit: number | null = null;

  for (const pp of product_prices) {
    const channelId = pp.price_channel_id;
    if (COMPARE_EXCLUDED_CHANNEL_IDS.has(channelId)) continue;
    if (!ACTIVE_COMPARE_CONFIG_BY_CHANNEL_ID.has(channelId)) continue;

    const competitorPrice = normalizePrice(pp.sale_price ?? pp.price);
    if (competitorPrice === null || competitorPrice <= 0) continue;
    if (competitorPrice <= ourSalePrice) continue;

    if (maxUnit === null || competitorPrice > maxUnit) {
      maxUnit = competitorPrice;
    }
  }

  return { maxUnit, ourSalePrice };
}

/**
 * Cart-level "Compare e comprove" totals.
 *
 * - `ourTotal` includes every line with a resolvable Qualidoc unit price (matches cart
 *   merchandise intent — not only lines that have a competitor).
 * - `competitorTotal` uses the highest qualifying competitor unit price when available;
 *   otherwise falls back to our unit price so non-comparable lines do not shrink the
 *   Qualidoc total relative to the cart footer.
 * - Savings only come from lines where a competitor price is higher than ours.
 */
export function aggregateCartCompareTotals(
  items: CartCompareLineInput[],
): {
  competitorTotal: number;
  ourTotal: number;
  savings: number;
  hasComparableLines: boolean;
} {
  let competitorTotal = 0;
  let ourTotal = 0;
  let hasAnyCompetitorLine = false;

  for (const item of items) {
    const qty = Math.max(0, Number(item.quantity) || 0);
    if (qty <= 0) continue;

    const { maxUnit, ourSalePrice } = getMaxQualifyingCompetitorUnitPrice(
      item.product_prices,
      { sale_price: item.sale_price, price: item.price },
    );
    if (ourSalePrice === null) continue;

    ourTotal += ourSalePrice * qty;

    if (maxUnit !== null) {
      competitorTotal += maxUnit * qty;
      hasAnyCompetitorLine = true;
    } else {
      // Keep line in both totals so Qualidoc sum stays aligned with the cart.
      competitorTotal += ourSalePrice * qty;
    }
  }

  const savings = Math.max(0, competitorTotal - ourTotal);
  const hasComparableLines =
    hasAnyCompetitorLine && competitorTotal > 0 && ourTotal > 0;

  return {
    competitorTotal,
    ourTotal,
    savings,
    hasComparableLines,
  };
}
